<template>
	<div class="fraud-comments">
		<!-- Integrated composer: a single-surface field where the textarea
		     and send button share one bordered frame. No outer label, no
		     boxed-in button — the whole thing reads as one input. -->
		<div class="fraud-comments__composer">
			<textarea
				class="fraud-comments__input"
				v-model="draft"
				placeholder="Skriv en note (Cmd/Ctrl+Enter for at sende)…"
				@keydown.meta.enter.prevent="send"
				@keydown.ctrl.enter.prevent="send"
			></textarea>
			<button
				type="button"
				class="fraud-comments__send"
				:disabled="!_canSend"
				@click="send"
			>Send</button>
		</div>

		<ul v-if="comments.length" class="fraud-comments__thread">
			<li v-for="c in comments" :key="c.id" :class="['fraud-comments__item', 'kind-' + c.kind]">
				<div class="fraud-comments__head">
					<span class="fraud-comments__who">{{ _who(c) }}</span>
					<span class="fraud-comments__kind" v-if="c.kind !== 'note'">{{ _kindLabel(c.kind) }}</span>
					<span class="fraud-comments__when">{{ _relative(c.created) }}</span>
				</div>
				<div class="fraud-comments__body">{{ c.body }}</div>
			</li>
		</ul>
		<div v-else class="fraud-comments__empty">Ingen kommentarer endnu.</div>
	</div>
</template>

<script>
export default {
	props: {
		comments: { type: Array, default: () => [] }
	},
	data() {
		return { draft: '' };
	},
	computed: {
		_canSend() {
			return (this.draft || '').trim().length > 0;
		}
	},
	methods: {
		_who(c) {
			if (c.employee_id === 0) return 'System';
			return c.employee_name || ('#' + c.employee_id);
		},
		_kindLabel(k) {
			return { dismiss: 'afviste', escalate: 'eskalerede', reopen: 'genåbnede', system: 'system' }[k] || k;
		},
		_relative(ts) {
			const d = new Date(ts);
			if (isNaN(d.getTime())) return ts;
			const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
			if (diffSec < 60)        return 'lige nu';
			if (diffSec < 3600)      return Math.floor(diffSec / 60)   + ' min siden';
			if (diffSec < 86400)     return Math.floor(diffSec / 3600) + ' timer siden';
			if (diffSec < 7 * 86400) return Math.floor(diffSec / 86400) + ' dage siden';
			return d.toLocaleDateString('da-DK');
		},
		send() {
			const body = (this.draft || '').trim();
			if (!body) return;
			this.$emit('send', body);
			this.draft = '';
		}
	}
};
</script>

<style lang="scss" scoped>
.fraud-comments {
	/* Lives inside a dark-themed side-window; the outer perfect-scrollbar
	   owns vertical space. Keep layout purely linear. */
}

/* ---------- composer ---------- */

.fraud-comments__composer {
	display: flex;
	align-items: stretch;
	background: #202127;
	border: 1px solid #2d2f36;
	border-radius: 8px;
	margin-bottom: 18px;
	overflow: hidden;
	transition: border-color 120ms ease;

	&:focus-within {
		border-color: #4a4d56;
	}
}

.fraud-comments__input {
	flex: 1;
	min-height: 64px;
	background: transparent;
	color: #ffffff;
	border: none;
	padding: 12px 14px;
	font-family: inherit;
	font-size: 14px;
	line-height: 1.5;
	resize: vertical;

	&::placeholder { color: #6b6e78; }
	&:focus        { outline: none; }
}

.fraud-comments__send {
	border: none;
	background: transparent;
	color: #f7223f;
	padding: 0 18px;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.3px;
	text-transform: uppercase;
	cursor: pointer;
	align-self: stretch;

	&:hover:not(:disabled) { background: rgba(247, 34, 63, 0.08); }
	&:disabled             { color: #4a4d56; cursor: not-allowed; }
}

/* ---------- thread ---------- */

.fraud-comments__empty {
	font-style: italic;
	color: #6b6e78;
	font-size: 13px;
}

.fraud-comments__thread {
	list-style: none;
	margin: 0;
	padding: 0;
}

.fraud-comments__item {
	padding: 14px 0;
	border-top: 1px solid #2d2f36;

	&:first-child { border-top: none; padding-top: 0; }
}

.fraud-comments__head {
	display: flex;
	gap: 8px;
	align-items: baseline;
	margin-bottom: 4px;
}

.fraud-comments__who {
	color: #ffffff;
	font-weight: 600;
	font-size: 14px;
	line-height: 1.3;
}

.fraud-comments__kind {
	color: #f7223f;
	font-size: 12px;
}

.fraud-comments__when {
	margin-left: auto;
	color: #6b6e78;
	font-size: 11px;
}

.fraud-comments__body {
	font-size: 14px;
	line-height: 1.55;
	white-space: pre-wrap;
	word-break: break-word;
	color: #ffffff;
}

/* Per-kind accents on the author name only — subtle, avoids the heavy
   coloured-avatar look while still signalling "this was a status change". */
.fraud-comments__item.kind-system   .fraud-comments__who { color: #6c8eb6; }
.fraud-comments__item.kind-dismiss  .fraud-comments__who { color: #a0a3ac; }
.fraud-comments__item.kind-escalate .fraud-comments__who { color: #d6a000; }
.fraud-comments__item.kind-reopen   .fraud-comments__who { color: #f7223f; }
</style>
